import type { ReactNode } from 'react';

/**
 * Endless horizontal loop, same mechanics as the Testimonials marquee: the
 * items render twice and the track slides by half its width. Pauses on hover,
 * press or keyboard focus, and for as long as a video is playing inside it.
 * With reduced motion the animation stops and the row becomes a normal
 * horizontal scroll.
 *
 * `interactive` keeps the second copy clickable (for video cards), hidden from
 * screen readers and the tab order instead of fully inert.
 */
export default function Loop({
  seconds,
  reverse,
  label,
  interactive,
  className = 'mt-10',
  children,
}: {
  seconds: number;
  reverse?: boolean;
  label: string;
  interactive?: boolean;
  className?: string;
  children: (copy: 0 | 1) => ReactNode;
}) {
  return (
    <div
      role="region"
      aria-label={label}
      className={`group/loop relative overflow-hidden motion-reduce:overflow-x-auto ${className}`}
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-black to-transparent motion-reduce:hidden sm:w-28" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-black to-transparent motion-reduce:hidden sm:w-28" />

      <div
        className="flex w-max animate-marquee items-stretch group-hover/loop:[animation-play-state:paused] group-active/loop:[animation-play-state:paused] group-focus-within/loop:[animation-play-state:paused] has-[iframe]:[animation-play-state:paused]"
        style={{
          animationDuration: `${seconds}s`,
          animationDirection: reverse ? 'reverse' : 'normal',
        }}
      >
        {children(0)}
        {interactive ? (
          <div aria-hidden="true" className="flex items-stretch motion-reduce:hidden">
            {children(1)}
          </div>
        ) : (
          <div aria-hidden="true" inert className="flex items-stretch motion-reduce:hidden">
            {children(1)}
          </div>
        )}
      </div>
    </div>
  );
}
